import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Users,
  Ticket,
  Heart,
  Share2,
  ChevronDown,
  ChevronUp,
  Minus,
  Plus,
  Loader2,
  Check,
  AlertCircle,
  Timer
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const EventDetail = ({ user }) => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const queryClient = useQueryClient();
  
  const [selectedTier, setSelectedTier] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showPurchase, setShowPurchase] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  // Fetch event details
  const { data: event, isLoading } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const response = await axios.get(`${API}/events/${eventId}`);
      return response.data;
    }
  });

  // Fetch user's wallets
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  // Check if following
  const { data: followingData } = useQuery({
    queryKey: ["followingEvents"],
    queryFn: async () => {
      const response = await axios.get(`${API}/events/following`);
      return response.data;
    }
  });

  useEffect(() => {
    if (followingData?.events && eventId) {
      setIsFollowing(followingData.events.some(e => e.event_id === eventId));
    }
  }, [followingData, eventId]);

  // Purchase mutation
  const purchaseMutation = useMutation({
    mutationFn: async (purchaseData) => {
      const response = await axios.post(`${API}/events/${eventId}/purchase`, purchaseData);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success("Tickets purchased!");
      queryClient.invalidateQueries(["event", eventId]);
      queryClient.invalidateQueries(["wallets"]);
      queryClient.invalidateQueries(["myTickets"]);
      navigate("/my-tickets", { state: { newOrder: data.order_id } });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Purchase failed");
    }
  });

  // Follow mutation
  const followMutation = useMutation({
    mutationFn: async () => {
      if (isFollowing) {
        await axios.delete(`${API}/events/${eventId}/follow`);
      } else {
        await axios.post(`${API}/events/${eventId}/follow`);
      }
    },
    onSuccess: () => {
      setIsFollowing(!isFollowing);
      toast.success(isFollowing ? "Unfollowed event" : "Following event");
      queryClient.invalidateQueries(["followingEvents"]);
    }
  });

  const jmdWallet = wallets?.find(w => w.currency === "JMD");

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      weekday: "long",
      month: "long", 
      day: "numeric",
      year: "numeric"
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", { 
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const formatCountdown = (seconds) => {
    if (seconds <= 0) return "Ended";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const handlePurchase = () => {
    if (!selectedTier) {
      toast.error("Please select a ticket tier");
      return;
    }
    if (!jmdWallet) {
      toast.error("No JMD wallet found");
      return;
    }

    const tier = event.ticket_tiers.find(t => t.tier_id === selectedTier);
    const subtotal = tier.price * quantity;
    const serviceFee = subtotal * 0.05;
    const total = subtotal + serviceFee;

    if (jmdWallet.available_balance < total) {
      toast.error(`Insufficient balance. Need J$${total.toLocaleString()}`);
      return;
    }

    purchaseMutation.mutate({
      event_id: eventId,
      tier_id: selectedTier,
      quantity: quantity,
      wallet_id: jmdWallet.wallet_id
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-white font-medium">Event not found</p>
        <Button onClick={() => navigate("/events")} className="mt-4">
          Back to Events
        </Button>
      </div>
    );
  }

  const selectedTierData = event.ticket_tiers?.find(t => t.tier_id === selectedTier);
  const subtotal = selectedTierData ? selectedTierData.price * quantity : 0;
  const serviceFee = subtotal * 0.05;
  const total = subtotal + serviceFee;

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="event-detail-page">
      {/* Hero Image */}
      <div className="relative">
        {event.flyer_url ? (
          <img 
            src={event.flyer_url} 
            alt={event.name}
            className="w-full h-64 object-cover"
          />
        ) : (
          <div className="w-full h-64 bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
            <Ticket className="w-16 h-16 text-white/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        
        {/* Back Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/events")}
          className="absolute top-4 left-4 bg-black/50 text-white hover:bg-black/70"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => followMutation.mutate()}
            className={`bg-black/50 hover:bg-black/70 ${isFollowing ? "text-red-400" : "text-white"}`}
          >
            <Heart className={`w-5 h-5 ${isFollowing ? "fill-current" : ""}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              navigator.share?.({ title: event.name, url: window.location.href }) ||
              toast.info("Share link copied!");
            }}
            className="bg-black/50 text-white hover:bg-black/70"
          >
            <Share2 className="w-5 h-5" />
          </Button>
        </div>

        {/* Event Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
              {event.category}
            </span>
            {event.age_restriction && (
              <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                {event.age_restriction}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-white">{event.name}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 max-w-lg mx-auto">
        {/* Date & Location */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="p-3 rounded-xl bg-white/5 border border-white/10">
            <Calendar className="w-5 h-5 text-emerald-400 mb-2" />
            <p className="text-sm font-medium text-white">{formatDate(event.event_date)}</p>
            <p className="text-xs text-muted-foreground">{formatTime(event.event_date)}</p>
          </div>
          <div className="p-3 rounded-xl bg-white/5 border border-white/10">
            <MapPin className="w-5 h-5 text-pink-400 mb-2" />
            <p className="text-sm font-medium text-white line-clamp-1">{event.venue}</p>
            <p className="text-xs text-muted-foreground">{event.parish}</p>
          </div>
        </div>

        {/* Description */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-white mb-2">About</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{event.description}</p>
        </div>

        {/* Ticket Tiers */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-white mb-3">Tickets</h2>
          <div className="space-y-2">
            {event.ticket_tiers?.map((tier) => {
              const isSelected = selectedTier === tier.tier_id;
              const isSoldOut = tier.quantity_remaining === 0;
              const isEarlyBirdActive = tier.early_bird && tier.early_bird_active;
              
              return (
                <div
                  key={tier.tier_id}
                  onClick={() => !isSoldOut && setSelectedTier(tier.tier_id)}
                  role="button"
                  tabIndex={isSoldOut ? -1 : 0}
                  onKeyDown={(e) => e.key === 'Enter' && !isSoldOut && setSelectedTier(tier.tier_id)}
                  className={`w-full p-4 rounded-xl border text-left transition-all cursor-pointer ${
                    isSelected 
                      ? "border-emerald-500 bg-emerald-500/10" 
                      : isSoldOut
                        ? "border-white/5 bg-white/5 opacity-50 cursor-not-allowed"
                        : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  data-testid={`tier-${tier.tier_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-white">{tier.name}</p>
                        {tier.early_bird && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                            isEarlyBirdActive 
                              ? "bg-amber-500/20 text-amber-400"
                              : "bg-white/10 text-muted-foreground line-through"
                          }`}>
                            Early Bird
                          </span>
                        )}
                      </div>
                      {tier.description && (
                        <p className="text-xs text-muted-foreground mt-1">{tier.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-muted-foreground">
                          {tier.quantity_remaining} left
                        </span>
                        {isEarlyBirdActive && tier.early_bird_seconds_remaining && (
                          <span className="text-xs text-amber-400 flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            {formatCountdown(tier.early_bird_seconds_remaining)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">
                        J${tier.price.toLocaleString()}
                      </p>
                      {isSoldOut && (
                        <span className="text-xs text-red-400">Sold Out</span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuantity(Math.max(1, quantity - 1));
                        }}
                        className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                      >
                        <Minus className="w-4 h-4 text-white" />
                      </button>
                      <span className="text-lg font-semibold text-white w-8 text-center">{quantity}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuantity(Math.min(10, quantity + 1, tier.quantity_remaining));
                        }}
                        className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                      >
                        <Plus className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Organizer */}
        <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="text-xs text-muted-foreground mb-1">Hosted by</p>
          <p className="text-sm font-medium text-white">{event.promoter_name}</p>
        </div>
      </div>

      {/* Purchase Bar */}
      {selectedTier && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-lg border-t border-white/10">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground">
                  {quantity}x {selectedTierData?.name}
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-xl font-bold text-white">J${total.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">(incl. 5% fee)</p>
                </div>
              </div>
              {jmdWallet && (
                <p className="text-xs text-muted-foreground">
                  Balance: J${jmdWallet.available_balance.toLocaleString()}
                </p>
              )}
            </div>
            <Button
              onClick={handlePurchase}
              disabled={purchaseMutation.isPending || !jmdWallet || jmdWallet.available_balance < total}
              className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base"
              data-testid="purchase-btn"
            >
              {purchaseMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Ticket className="w-5 h-5 mr-2" />
                  Buy Tickets
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
